import { BaseElement } from 'base';
import Swiper from 'swiper-core';
import { sectionFragment } from 'section-fragment';

const REDUCE = '(prefers-reduced-motion: reduce)';
const BUTTON_TAG = 'action-button';
const CARD_TAG = 'product-card';
const VIEWPORT = '[data-upsell-carousel-viewport]';
const LOOP_MIN = 4;
const SLIDE_SPEED = 300;

class UpsellCarousel extends BaseElement {
  #action = null;
  #swiper = null;
  #viewport = null;
  #prev = null;
  #next = null;
  #reduceMq = null;

  onConnect() {
    this.#reduceMq = window.matchMedia(REDUCE);
    this.listen(this.#reduceMq, 'change', this.#onReduceChange);
    this.listen(this, 'keydown', this.#onKeydown);
    this.listen(window, 'resize', this.#onWindowResize);
    this.cleanup(() => this.#unmount());
    this.#mount();
    if (this.dataset.sectionId)
      this.subscribeLive('cart:updated', this.#render);
  }

  run(action) {
    this.#action = action;
    return this;
  }

  #mount() {
    this.#viewport = this.q(VIEWPORT);
    this.#prev = this.q(`${BUTTON_TAG}.upsell-carousel__arrow--prev`);
    this.#next = this.q(`${BUTTON_TAG}.upsell-carousel__arrow--next`);
    this.#bindArrows();
    this.#bindCards();
    this.#initSwiper();
  }

  #unmount() {
    this.#swiper?.destroy(true, true);
    this.#swiper = null;
  }

  #initSwiper() {
    if (!this.#viewport) return;
    const inset = this.#readLength('--upsell-carousel-padding-inline');
    this.#swiper = new Swiper(this.#viewport, {
      slidesPerView: 'auto',
      spaceBetween: this.#readLength('--upsell-carousel-gap'),
      slidesOffsetBefore: inset,
      slidesOffsetAfter: inset,
      loop: this.#canLoop(),
      watchOverflow: true,
      watchSlidesProgress: true,
      speed: this.#slideSpeed(),
      on: {
        afterInit: instance => {
          this.#syncEdges(instance);
          this.#syncInert(instance);
        },
        progress: instance => this.#syncInert(instance),
        slideChange: instance => this.#syncEdges(instance),
        fromEdge: instance => this.#syncEdges(instance),
        reachBeginning: instance => this.#syncEdges(instance),
        reachEnd: instance => this.#syncEdges(instance),
        lock: instance => this.#syncEdges(instance),
        unlock: instance => this.#syncEdges(instance),
        resize: instance => this.#onResize(instance)
      }
    });
  }

  #bindArrows() {
    const prev = this.#prev;
    const next = this.#next;
    if (!prev && !next) return;
    customElements.whenDefined(BUTTON_TAG).then(() => {
      if (!this.isConnected || prev !== this.#prev) return;
      prev?.run(() => this.#step(-1));
      next?.run(() => this.#step(1));
      this.#syncEdges();
    });
  }

  #bindCards() {
    const cards = this.qa(CARD_TAG);
    if (!cards.length) return;
    customElements.whenDefined(CARD_TAG).then(() => {
      if (!this.isConnected) return;
      for (const card of cards) card.run(change => this.#action?.(change));
    });
  }

  #render({ sections } = {}) {
    const fresh = sectionFragment(sections, this.dataset.sectionId, this.id);
    if (!fresh) return;
    const hadFocus = this.contains(document.activeElement);
    this.#unmount();
    this.replaceChildren(...fresh.childNodes);
    this.hidden = fresh.hidden;
    this.dataset.count = fresh.dataset.count ?? '0';
    this.#mount();
    if (hadFocus && !this.hidden)
      this.#viewport?.focus({ preventScroll: true });
  }

  #canLoop() {
    const count = Number(this.dataset.count) || 0;
    if (count < LOOP_MIN || !this.#viewport) return false;
    const slot =
      this.#readLength('--upsell-carousel-card-width') +
      this.#readLength('--upsell-carousel-gap');
    if (!slot) return false;
    return count > Math.ceil(this.#viewport.clientWidth / slot);
  }

  #onWindowResize() {
    const instance = this.#swiper;
    if (instance && !instance.destroyed) this.#onResize(instance);
  }

  #onResize(instance) {
    if (instance.params.loop !== this.#canLoop()) {
      queueMicrotask(() => {
        if (!this.isConnected || instance !== this.#swiper) return;
        this.#unmount();
        this.#initSwiper();
      });
      return;
    }
    const gap = this.#readLength('--upsell-carousel-gap');
    const inset = this.#readLength('--upsell-carousel-padding-inline');
    if (
      instance.params.spaceBetween !== gap ||
      instance.params.slidesOffsetBefore !== inset
    ) {
      instance.params.spaceBetween = gap;
      instance.params.slidesOffsetBefore = inset;
      instance.params.slidesOffsetAfter = inset;
      instance.update();
    }
    this.#syncEdges(instance);
    this.#syncInert(instance);
  }

  #syncInert(instance) {
    const visibleClass = instance.params.slideVisibleClass;
    for (const slide of instance.slides) {
      slide.toggleAttribute('inert', !slide.classList.contains(visibleClass));
    }
  }

  #syncEdges(instance = this.#swiper) {
    if (!instance || instance.destroyed) return;
    const locked = instance.isLocked;
    const loop = instance.params.loop;
    this.#prev?.setDisabled?.(locked || (!loop && instance.isBeginning));
    this.#next?.setDisabled?.(locked || (!loop && instance.isEnd));
  }

  #slideSpeed() {
    return this.#reduceMq?.matches ? 0 : SLIDE_SPEED;
  }

  #onReduceChange() {
    if (this.#swiper) this.#swiper.params.speed = this.#slideSpeed();
  }

  #readLength(name) {
    return parseFloat(getComputedStyle(this).getPropertyValue(name)) || 0;
  }

  #step(direction) {
    if (!this.#swiper || this.#swiper.isLocked) return;
    if (direction > 0) this.#swiper.slideNext();
    else this.#swiper.slidePrev();
  }

  #onKeydown(event) {
    if (!this.#viewport || event.target !== this.#viewport) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (!this.#swiper || this.#swiper.isLocked) return;
    event.preventDefault();
    const forward = this.#swiper.rtlTranslate
      ? event.key === 'ArrowLeft'
      : event.key === 'ArrowRight';
    this.#step(forward ? 1 : -1);
  }
}

if (!customElements.get('upsell-carousel')) {
  customElements.define('upsell-carousel', UpsellCarousel);
}
